import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { achievementsStore } from './achievementsStore';
import { notifyAchievement } from './notifyAchievement';

// A "questionable duration" session — long enough that being here is the joke.
const SESSION_MS = 2 * 60 * 60 * 1000; // 2h
const DEJA_VU_VISITS = 20; // same page this many times today
const SPEED_NAVS = 5; // route changes...
const SPEED_WINDOW_MS = 10_000; // ...within this window

// Renders nothing. Mounted inside the Router (so it can read route changes) and does nothing at all
// until the user has discovered an easter egg — keeping the Backoffice quiet for everyone else.
export function AchievementTracker() {
    const location = useLocation();
    const [tracking, setTracking] = useState(achievementsStore.isTrackingEnabled());
    const navTimes = useRef<number[]>([]);

    // Flip on the moment tracking becomes enabled (e.g. the user just found their first egg).
    useEffect(() => achievementsStore.subscribe(() => setTracking(achievementsStore.isTrackingEnabled())), []);

    // Time-of-day + long-session achievements — (re)evaluated once tracking is on.
    useEffect(() => {
        if (!tracking) return;
        const hour = new Date().getHours();
        if (hour < 7) notifyAchievement('early-bird');
        if (hour >= 22) notifyAchievement('night-watch');
        const t = setTimeout(() => notifyAchievement('still-there'), SESSION_MS);
        return () => clearTimeout(t);
    }, [tracking]);

    // Per-navigation achievements: repeat visits (Déjà Vu) and rapid navigation (I Am Speed).
    useEffect(() => {
        if (!tracking) return;
        if (achievementsStore.bumpPageVisit(location.pathname) >= DEJA_VU_VISITS) {
            notifyAchievement('deja-vu');
        }
        const now = Date.now();
        navTimes.current = [...navTimes.current, now].filter((ts) => now - ts <= SPEED_WINDOW_MS);
        if (navTimes.current.length >= SPEED_NAVS) notifyAchievement('i-am-speed');
    }, [location.pathname, tracking]);

    return null;
}
